/**
 * KPI Hooks
 *
 * TanStack Query hooks for KPI definitions, measurements, analytics, and the
 * AI Builder flow. Phase 24.7 (Track 1) deleted the legacy "enable/disable
 * config" hooks (`useKpiConfigs`, `useKpiConfig`, `useEnableKpi`,
 * `useUpdateKpiConfig`, `useDisableKpi`, `useKpiTemplates`) — scope writes now
 * go through `useUpdateKpiScope` (PUT /api/kpi-definitions/:id/scope) and KPI
 * lifecycle is handled via `useGenerateKpi` / `useUpdateKpiDefinition` /
 * `useDisableKpiDefinition` (the legacy `KpiSettingsPage` was removed).
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../lib/hooks/useApi';
import type {
  KpiDefinition,
  OrgKpiConfig,
  KpiMeasurementWithDefinition,
  UpdateKpiScope,
  KpiMeasurementQuery,
  KpiAnalyticsQuery,
  KpiAnalyticsResponse,
  CumulativeKpiQuery,
  CumulativeKpiResponse,
  KpiWhy,
} from '@guild/shared';

// ============================================
// PHASE 24.7 (Track 1) — Library response shape
// ============================================

/**
 * Shape returned by GET /api/kpi-definitions/org (Phase 24.7 / Chunk 2).
 * The endpoint LEFT JOINs org_kpi_configs and resolves zone metadata server-side
 * so the Library card can render scope chips + the Q11 stale-zone badge without
 * a follow-up round-trip.
 */
export type KpiLibraryEntry = KpiDefinition & {
  measurementCount: number;
  /** Scoped zone ids. `null` indicates no org_kpi_configs row (defensive — should not happen post-Chunk 1). */
  stationIds: string[] | null;
  /** Optional position scope. Empty/null means "all positions". */
  positionCodes: string[] | null;
  /** Whether the KPI is currently enabled in this org. `null` mirrors the missing-config edge case. */
  isEnabled: boolean | null;
  /** Resolved zone metadata for every entry in `stationIds`. */
  stations: Array<{ id: string; name: string; isActive: boolean }>;
};

// ============================================
// QUERY KEYS
// ============================================

export const kpiKeys = {
  all: ['kpis'] as const,
  // Definitions
  definitions: () => [...kpiKeys.all, 'definitions'] as const,
  definition: (id: string) => [...kpiKeys.definitions(), id] as const,
  orgDefinitions: () => [...kpiKeys.definitions(), 'org'] as const,
  lookup: () => [...kpiKeys.definitions(), 'lookup'] as const,
  // Configs — retained as an invalidation namespace for `useGenerateKpi` /
  // `useUpdateKpiScope`. Phase 24.7 (Track 1) deleted the consumer hooks
  // (`useKpiConfigs`, `useKpiConfig`) but kept the namespace so caches that
  // still key off ['kpis', 'configs'] from older builds get invalidated.
  configs: () => [...kpiKeys.all, 'configs'] as const,
  // Measurements
  measurements: () => [...kpiKeys.all, 'measurements'] as const,
  measurementsList: (filters: Partial<KpiMeasurementQuery>) =>
    [...kpiKeys.measurements(), filters] as const,
  measurement: (id: string) => [...kpiKeys.measurements(), id] as const,
  // Analytics
  analytics: (params: Partial<KpiAnalyticsQuery>) =>
    [...kpiKeys.all, 'analytics', params] as const,
  // Phase 30 Wave 5 — cumulative roll-up endpoint (per shift-window)
  cumulative: (params: Partial<CumulativeKpiQuery>) =>
    [...kpiKeys.all, 'cumulative', params] as const,
  // Builder
  builderConversation: () => [...kpiKeys.all, 'builder'] as const,
};

// ============================================
// RESPONSE TYPES
// ============================================

/**
 * One item of GET /api/kpi-measurements (Phase 41A, spec §4.1.3). Fields the
 * previous API does not send are optional: during a promote the new web meets
 * the previous API for a few minutes (spec S16).
 */
export type KpiMeasurementListItem = Omit<KpiMeasurementWithDefinition, 'measurementData'> & {
  triggerId: string | null;
  /** The trigger's clip window (ISO UTC); null when the result has no trigger. */
  footageStart?: string | null;
  footageEnd?: string | null;
  /**
   * The stored observation status (spec D12). APIs that still send
   * `measurementData` omit it; there, fall back to
   * `measurementData.observationStatus` (narrow the `unknown` first).
   */
  observationStatus?: string | null;
  /** The structured reason behind the result (spec §4.2.3); read as `why ?? { shape: 'none' }`. */
  why?: KpiWhy;
  /** Sent only by APIs from before spec D12. */
  measurementData?: unknown;
};

export interface KpiMeasurementsResponse {
  measurements: KpiMeasurementListItem[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
  };
}

/**
 * True when `previous` is the same list as `current` with a different `offset`
 * (every other filter equal). Only then may the previous page stand in while
 * the next one loads: rows from another KPI, window or status filter never do.
 */
function isSameListOtherPage(
  previous: Partial<KpiMeasurementQuery> | undefined,
  current: Partial<KpiMeasurementQuery>
): boolean {
  if (!previous) return false;
  const keys = new Set([...Object.keys(previous), ...Object.keys(current)]) as Set<
    keyof KpiMeasurementQuery
  >;
  keys.delete('offset');
  return [...keys].every((key) => previous[key] === current[key]);
}

// ============================================
// KPI DEFINITIONS HOOKS
// ============================================

/**
 * Fetch all KPI definitions (system templates + org-specific)
 */
export function useKpiDefinitions() {
  const api = useApi();

  return useQuery({
    queryKey: kpiKeys.definitions(),
    queryFn: () => api.get<KpiDefinition[]>('/api/kpi-definitions'),
  });
}

/**
 * Fetch single KPI definition
 */
export function useKpiDefinition(id: string | null) {
  const api = useApi();

  return useQuery({
    queryKey: kpiKeys.definition(id ?? ''),
    queryFn: () => api.get<KpiDefinition>(`/api/kpi-definitions/${id}`),
    enabled: !!id,
  });
}

// ============================================
// KPI MEASUREMENTS HOOKS
// ============================================

/**
 * Fetch KPI measurements with filters
 *
 * `status` is a comma list of status tokens (`normal`, `warning`, `critical`,
 * `not_observed`). `options.enabled` (default true) holds the request back.
 * While another page of the same list loads, the previous page stays as
 * placeholder data (`isPlaceholderData`), so a table and its pager stay mounted.
 */
export function useKpiMeasurements(
  filters: Partial<KpiMeasurementQuery> = {},
  options: { enabled?: boolean } = {}
) {
  const api = useApi();

  return useQuery({
    queryKey: kpiKeys.measurementsList(filters),
    queryFn: () => {
      const params = new URLSearchParams();

      if (filters.kpiCode) params.set('kpiCode', filters.kpiCode);
      if (filters.stationId) params.set('stationId', filters.stationId);
      if (filters.operatorId) params.set('operatorId', filters.operatorId);
      if (filters.shiftId) params.set('shiftId', filters.shiftId);
      if (filters.startDate) params.set('startDate', filters.startDate);
      if (filters.endDate) params.set('endDate', filters.endDate);
      if (filters.thresholdStatus) params.set('thresholdStatus', filters.thresholdStatus);
      if (filters.status) params.set('status', filters.status);
      if (filters.limit) params.set('limit', String(filters.limit));
      if (filters.offset) params.set('offset', String(filters.offset));

      const queryString = params.toString();
      const endpoint = queryString
        ? `/api/kpi-measurements?${queryString}`
        : '/api/kpi-measurements';

      return api.get<KpiMeasurementsResponse>(endpoint);
    },
    enabled: options.enabled ?? true,
    placeholderData: (previousData, previousQuery) =>
      isSameListOtherPage(previousQuery?.queryKey[2], filters) ? previousData : undefined,
  });
}

/**
 * Fetch single KPI measurement
 */
export function useKpiMeasurement(id: string | null) {
  const api = useApi();

  return useQuery({
    queryKey: kpiKeys.measurement(id ?? ''),
    queryFn: () => api.get<KpiMeasurementWithDefinition>(`/api/kpi-measurements/${id}`),
    enabled: !!id,
  });
}

// ============================================
// KPI ANALYTICS HOOKS
// ============================================

/**
 * Fetch KPI analytics with aggregation
 */
export function useKpiAnalytics(params: KpiAnalyticsQuery) {
  const api = useApi();

  return useQuery({
    queryKey: kpiKeys.analytics(params),
    queryFn: () => {
      const searchParams = new URLSearchParams();
      searchParams.set('kpiCode', params.kpiCode);
      searchParams.set('groupBy', params.groupBy);

      if (params.startDate) searchParams.set('startDate', params.startDate);
      if (params.endDate) searchParams.set('endDate', params.endDate);
      if (params.stationId) searchParams.set('stationId', params.stationId);
      if (params.operatorId) searchParams.set('operatorId', params.operatorId);
      if (params.shiftId) searchParams.set('shiftId', params.shiftId);

      return api.get<KpiAnalyticsResponse>(
        `/api/kpi-measurements/analytics?${searchParams.toString()}`
      );
    },
    enabled: !!params.kpiCode,
  });
}

/**
 * Phase 30 Wave 5 — Fetch per-shift-window cumulative KPI roll-up.
 *
 * Calls `GET /api/kpi-measurements/analytics/cumulative` (Chunk C). The endpoint
 * respects `kpi.aggregationType` (sum → SUM, count → COUNT, average → AVG, etc.)
 * over measured runs only, so a window with no measured run is not returned
 * (G-35), and groups by `shift_window_start` (write-time canonical instant).
 *
 * Read-time tz contract (EM Item 7b): `shiftWindowStart` is ISO UTC. Frontend
 * formats display per browser timezone. Template-level
 * `assessment_schedules.timezone` is NOT honored at read time — write-time
 * precedence already resolved the canonical shift window.
 */
export function useCumulativeKpi(params: Partial<CumulativeKpiQuery> & { enabled?: boolean }) {
  const api = useApi();
  const { enabled = true, ...query } = params;

  return useQuery({
    queryKey: kpiKeys.cumulative(query),
    queryFn: () => {
      const searchParams = new URLSearchParams();
      if (query.kpiCode) searchParams.set('kpiCode', query.kpiCode);
      if (query.startDate) searchParams.set('startDate', query.startDate);
      if (query.endDate) searchParams.set('endDate', query.endDate);
      if (query.bucket) searchParams.set('bucket', query.bucket);
      if (query.stationId) searchParams.set('stationId', query.stationId);
      if (query.operatorId) searchParams.set('operatorId', query.operatorId);
      if (query.shiftId) searchParams.set('shiftId', query.shiftId);

      return api.get<CumulativeKpiResponse>(
        `/api/kpi-measurements/analytics/cumulative?${searchParams.toString()}`
      );
    },
    enabled: enabled && !!query.kpiCode && !!query.startDate && !!query.endDate,
  });
}

// ============================================
// KPI LIBRARY + BUILDER HOOKS (Phase 24.1)
// ============================================

/**
 * Fetch org-owned KPI definitions (for library page)
 *
 * Phase 24.7 (Track 1): the response now includes `stationIds`, `positionCodes`,
 * `isEnabled`, and `stations` (zone metadata) for the Library card scope display
 * + Q11 stale-zone badge. See {@link KpiLibraryEntry}.
 */
export function useOrgKpiDefinitions(includeInactive = false) {
  const api = useApi();
  return useQuery({
    queryKey: [...kpiKeys.orgDefinitions(), { includeInactive }],
    queryFn: () => api.get<KpiLibraryEntry[]>(
      `/api/kpi-definitions/org${includeInactive ? '?includeInactive=true' : ''}`
    ),
  });
}

/**
 * Fetch KPI lookup (for dropdowns)
 */
export function useKpiLookup() {
  const api = useApi();
  return useQuery({
    queryKey: kpiKeys.lookup(),
    queryFn: () => api.get<Array<{ id: string; name: string; code: string; category: string | null; unit: string | null }>>(
      '/api/kpi-definitions/lookup'
    ),
    staleTime: 2 * 60 * 1000,
  });
}

/**
 * KPI Builder conversation mutation
 */
export function useKpiBuilderConversation() {
  const api = useApi();
  return useMutation({
    mutationFn: (data: { messages: Array<{ role: 'ai' | 'user'; text: string }> }) =>
      api.post<{
        done: boolean;
        question?: string;
        questionIndex?: number;
        totalQuestions?: number;
        kpiDefinition?: Record<string, unknown>;
        message?: string;
      }>('/api/kpi-definitions/conversation', data),
  });
}

/**
 * Generate (persist) KPI from builder output
 */
export function useGenerateKpi() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      api.post<KpiDefinition>('/api/kpi-definitions/generate', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kpiKeys.definitions() });
      queryClient.invalidateQueries({ queryKey: kpiKeys.configs() });
    },
  });
}

/**
 * Update KPI definition
 */
export function useUpdateKpiDefinition() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<KpiDefinition> }) =>
      api.put<KpiDefinition>(`/api/kpi-definitions/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kpiKeys.definitions() });
    },
  });
}

/**
 * Disable (soft delete) KPI definition
 */
export function useDisableKpiDefinition() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.delete<{ success: boolean }>(`/api/kpi-definitions/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kpiKeys.definitions() });
    },
  });
}

/**
 * Update KPI scope — zones + (optional) positions
 *
 * Phase 24.7 (Track 1): hits the dedicated `PUT /api/kpi-definitions/:id/scope`
 * endpoint, which writes the matching org_kpi_configs row. Surfaces Zod errors
 * verbatim ("KPIs must have at least one zone") so the Library inline form can
 * display them.
 */
export function useUpdateKpiScope() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateKpiScope }) =>
      api.put<OrgKpiConfig>(`/api/kpi-definitions/${id}/scope`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kpiKeys.definitions() });
      queryClient.invalidateQueries({ queryKey: kpiKeys.orgDefinitions() });
      queryClient.invalidateQueries({ queryKey: kpiKeys.configs() });
    },
  });
}
